import { GraficoBarrasIngresoGasto } from '@/components/dominio/GraficoBarrasIngresoGasto';
import { FiltroPeriodos } from '@/components/filtros/FiltroPeriodos';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePeriodosDisponibles } from '@/hooks/useAreaCosteo';
import {
  useUnidadesProduccion,
  useUnidadesProduccionMutaciones,
} from '@/hooks/useUnidadesProduccion';
import type { PeriodoMaqSel } from '@/lib/cliente';
import { formatearCRC } from '@adelante/shared';
import type { MaquinaUnidadProduccion, UnidadProduccion } from '@adelante/shared';
import { CaretDown, CaretRight, CaretUp, CircleNotch, Pencil, Trash } from '@phosphor-icons/react';
import { Fragment, useMemo, useState } from 'react';
import { toast } from 'sonner';

const PERIODO_MAQ_VACIO: PeriodoMaqSel = { aniosSel: [], mesesSel: [], diasSel: [] };

/** Cifras agregadas (de una unidad, o de las máquinas sin unidad). */
interface Cifras {
  horas: number;
  ingresoPartes: number;
  ingresoRecursos: number;
  ingreso: number;
  gasto: number;
  margen: number;
}

function sumar(maquinas: MaquinaUnidadProduccion[]): Cifras {
  const c = maquinas.reduce(
    (a, m) => ({
      horas: a.horas + m.totalHoras,
      ingresoPartes: a.ingresoPartes + m.totalIngresoPartesLcy,
      ingresoRecursos: a.ingresoRecursos + m.totalIngresoRecursosLcy,
      ingreso: a.ingreso + m.totalIngresoLcy,
      gasto: a.gasto + m.totalGastoLcy,
      margen: 0,
    }),
    { horas: 0, ingresoPartes: 0, ingresoRecursos: 0, ingreso: 0, gasto: 0, margen: 0 },
  );
  c.margen = c.ingreso - c.gasto;
  return c;
}

function etiquetaMaquina(m: { codigoMaquina: string; nombreMaquina: string | null }): string {
  return m.nombreMaquina ? `${m.codigoMaquina} — ${m.nombreMaquina}` : m.codigoMaquina;
}

function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

function Margen({ valor }: { valor: number }) {
  return (
    <span className={valor < 0 ? 'text-red-600' : 'text-green-600'}>{formatearCRC(valor)}</span>
  );
}

function TarjetaKpi({
  etiqueta,
  valor,
  tono = 'default',
}: {
  etiqueta: string;
  valor: string;
  tono?: 'default' | 'positivo' | 'negativo';
}) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <p className="text-xs text-muted-foreground">{etiqueta}</p>
        <p
          className={`text-xl font-semibold tabular-nums ${
            tono === 'positivo' ? 'text-green-600' : tono === 'negativo' ? 'text-red-600' : ''
          }`}
        >
          {valor}
        </p>
      </CardContent>
    </Card>
  );
}

type ColumnaUnidad =
  | 'nombre'
  | 'maquinas'
  | 'horas'
  | 'ingresoPartes'
  | 'ingresoRecursos'
  | 'ingreso'
  | 'gasto'
  | 'margen';

interface FilaUnidad extends Cifras {
  unidad: UnidadProduccion;
  miembros: MaquinaUnidadProduccion[];
  nombre: string;
  maquinas: number;
}

/**
 * Unidades de Producción: análisis de conjuntos de máquinas (ej. Planta Blend
 * + silos + camión) como una sola unidad, y administración de qué máquina
 * pertenece a cuál. Las unidades son propias de la app (no existen en BC).
 */
export function UnidadesProduccionPantalla() {
  const [vista, setVista] = useState<'analisis' | 'administrar'>('analisis');
  const [periodoMaq, setPeriodoMaq] = useState<PeriodoMaqSel>(PERIODO_MAQ_VACIO);
  const { data: fechasData } = usePeriodosDisponibles();
  const { data, isLoading, error } = useUnidadesProduccion(periodoMaq);

  const unidades = data?.data?.unidades ?? [];
  const maquinas = data?.data?.maquinas ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
        Cargando…
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Unidades de Producción</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Agrupa máquinas que operan como un solo proceso (ej. planta de concreto + silos de
            cemento) para ver su ingreso, gasto y margen en conjunto. Cada máquina pertenece a una
            sola unidad. Las unidades se administran aquí, no en Business Central.
          </p>
        </div>
        <div className="flex gap-1 rounded-md border border-zinc-200 p-1">
          <Button
            size="sm"
            variant={vista === 'analisis' ? 'default' : 'ghost'}
            onClick={() => setVista('analisis')}
          >
            Análisis
          </Button>
          <Button
            size="sm"
            variant={vista === 'administrar' ? 'default' : 'ghost'}
            onClick={() => setVista('administrar')}
          >
            Administrar
          </Button>
        </div>
      </div>

      {vista === 'analisis' ? (
        <VistaAnalisis
          unidades={unidades}
          maquinas={maquinas}
          periodoMaq={periodoMaq}
          fechas={fechasData?.data ?? []}
          onPeriodo={setPeriodoMaq}
          onAdministrar={() => setVista('administrar')}
        />
      ) : (
        <VistaAdministrar unidades={unidades} maquinas={maquinas} />
      )}
    </div>
  );
}

function VistaAnalisis({
  unidades,
  maquinas,
  periodoMaq,
  fechas,
  onPeriodo,
  onAdministrar,
}: {
  unidades: UnidadProduccion[];
  maquinas: MaquinaUnidadProduccion[];
  periodoMaq: PeriodoMaqSel;
  fechas: string[];
  onPeriodo: (p: PeriodoMaqSel) => void;
  onAdministrar: () => void;
}) {
  const [orden, setOrden] = useState<{ columna: ColumnaUnidad; direccion: 'asc' | 'desc' }>({
    columna: 'gasto',
    direccion: 'desc',
  });
  const [abiertas, setAbiertas] = useState<Set<number>>(new Set());
  const [sinUnidadAbierta, setSinUnidadAbierta] = useState(false);

  const filas = useMemo<FilaUnidad[]>(
    () =>
      unidades.map((u) => {
        const miembros = maquinas.filter((m) => m.skUnidadProduccion === u.skUnidadProduccion);
        return {
          ...sumar(miembros),
          unidad: u,
          miembros,
          nombre: u.nombre,
          maquinas: miembros.length,
        };
      }),
    [unidades, maquinas],
  );

  const sinUnidad = useMemo(
    () =>
      maquinas.filter(
        (m) => m.skUnidadProduccion === null && (m.totalIngresoLcy !== 0 || m.totalGastoLcy !== 0),
      ),
    [maquinas],
  );
  const cifrasSin = useMemo(() => sumar(sinUnidad), [sinUnidad]);
  const totalUnidades = useMemo(() => sumar(filas.flatMap((f) => f.miembros)), [filas]);

  const ordenadas = useMemo(() => {
    const signo = orden.direccion === 'asc' ? 1 : -1;
    return [...filas].sort((a, b) => {
      const va = a[orden.columna];
      const vb = b[orden.columna];
      return typeof va === 'string'
        ? signo * va.localeCompare(String(vb), 'es')
        : signo * ((va as number) - (vb as number));
    });
  }, [filas, orden]);

  const grafico = useMemo(
    () =>
      [...filas]
        .filter((f) => f.ingreso !== 0 || f.gasto !== 0)
        .sort((a, b) => b.gasto - a.gasto)
        .slice(0, 12)
        .map((f) => ({ etiqueta: f.nombre, ingreso: f.ingreso, gasto: f.gasto })),
    [filas],
  );

  const ordenarPor = (columna: ColumnaUnidad) =>
    setOrden((p) =>
      p.columna === columna
        ? { columna, direccion: p.direccion === 'asc' ? 'desc' : 'asc' }
        : { columna, direccion: columna === 'nombre' ? 'asc' : 'desc' },
    );

  const alternar = (sk: number) =>
    setAbiertas((p) => {
      const n = new Set(p);
      if (n.has(sk)) n.delete(sk);
      else n.add(sk);
      return n;
    });

  const encabezado = (columna: ColumnaUnidad, etiqueta: string, derecha = true) => (
    <TableHead
      className={`text-xs cursor-pointer select-none hover:bg-zinc-100 ${derecha ? 'text-right' : ''}`}
      onClick={() => ordenarPor(columna)}
    >
      {etiqueta}
      {orden.columna === columna &&
        (orden.direccion === 'asc' ? (
          <CaretUp className="h-3 w-3 inline ml-1" weight="bold" />
        ) : (
          <CaretDown className="h-3 w-3 inline ml-1" weight="bold" />
        ))}
    </TableHead>
  );

  const celdasCifras = (c: Cifras) => (
    <>
      <TableCell className="text-right text-xs tabular-nums">
        {c.horas.toLocaleString('es-CR', { maximumFractionDigits: 1 })}
      </TableCell>
      <TableCell className="text-right text-xs tabular-nums">
        {formatearCRC(c.ingresoPartes)}
      </TableCell>
      <TableCell className="text-right text-xs tabular-nums">
        {formatearCRC(c.ingresoRecursos)}
      </TableCell>
      <TableCell className="text-right text-xs tabular-nums font-medium">
        {formatearCRC(c.ingreso)}
      </TableCell>
      <TableCell className="text-right text-xs tabular-nums">{formatearCRC(c.gasto)}</TableCell>
      <TableCell className="text-right text-xs tabular-nums">
        <Margen valor={c.margen} />
      </TableCell>
    </>
  );

  return (
    <>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Período (solo Maquinaria):</span>
        <FiltroPeriodos
          fechasDisponibles={fechas}
          aniosSel={periodoMaq.aniosSel}
          mesesSel={periodoMaq.mesesSel}
          diasSel={periodoMaq.diasSel}
          onChange={onPeriodo}
        />
        <span className="text-xs text-muted-foreground">
          — sin selección, muestra el histórico completo.
        </span>
      </div>

      {unidades.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground space-y-3">
            <p>Todavía no hay unidades de producción. Crea la primera y asígnale máquinas.</p>
            <Button size="sm" onClick={onAdministrar}>
              Crear unidades y asignar máquinas
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <TarjetaKpi etiqueta="Unidades" valor={String(unidades.length)} />
            <TarjetaKpi
              etiqueta="Ingreso de unidades"
              valor={formatearCRC(totalUnidades.ingreso)}
              tono="positivo"
            />
            <TarjetaKpi etiqueta="Gasto de unidades" valor={formatearCRC(totalUnidades.gasto)} />
            <TarjetaKpi
              etiqueta="Margen de unidades"
              valor={formatearCRC(totalUnidades.margen)}
              tono={totalUnidades.margen < 0 ? 'negativo' : 'positivo'}
            />
          </div>

          {grafico.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Ingreso vs. Gasto por unidad</CardTitle>
              </CardHeader>
              <CardContent>
                <GraficoBarrasIngresoGasto datos={grafico} />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                Detalle por unidad
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  click en una unidad para ver sus máquinas
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {encabezado('nombre', 'Unidad', false)}
                    {encabezado('maquinas', 'Máq.')}
                    {encabezado('horas', 'Horas')}
                    {encabezado('ingresoPartes', 'Ingreso Partes')}
                    {encabezado('ingresoRecursos', 'Ingreso Recursos')}
                    {encabezado('ingreso', 'Ingreso Total')}
                    {encabezado('gasto', 'Gasto')}
                    {encabezado('margen', 'Margen')}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ordenadas.map((f) => {
                    const abierta = abiertas.has(f.unidad.skUnidadProduccion);
                    return (
                      <Fragment key={f.unidad.skUnidadProduccion}>
                        <TableRow
                          className="cursor-pointer hover:bg-zinc-50"
                          onClick={() => alternar(f.unidad.skUnidadProduccion)}
                        >
                          <TableCell className="text-xs font-medium">
                            {abierta ? (
                              <CaretDown className="h-3 w-3 inline mr-1" weight="bold" />
                            ) : (
                              <CaretRight className="h-3 w-3 inline mr-1" weight="bold" />
                            )}
                            {f.unidad.nombre}
                            {f.unidad.descripcion && (
                              <span className="ml-2 font-normal text-muted-foreground">
                                {f.unidad.descripcion}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-xs tabular-nums">
                            {f.maquinas}
                          </TableCell>
                          {celdasCifras(f)}
                        </TableRow>
                        {abierta &&
                          [...f.miembros]
                            .sort((a, b) => b.totalGastoLcy - a.totalGastoLcy)
                            .map((m) => (
                              <TableRow key={m.codigoMaquina} className="bg-zinc-50/70">
                                <TableCell className="text-xs pl-8 text-muted-foreground">
                                  {etiquetaMaquina(m)}
                                </TableCell>
                                <TableCell />
                                {celdasCifras(sumar([m]))}
                              </TableRow>
                            ))}
                        {abierta && f.miembros.length === 0 && (
                          <TableRow className="bg-zinc-50/70">
                            <TableCell colSpan={8} className="text-xs pl-8 text-muted-foreground">
                              Esta unidad no tiene máquinas asignadas.
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                  {sinUnidad.length > 0 && (
                    <>
                      <TableRow
                        className="border-t-2 cursor-pointer text-muted-foreground hover:bg-zinc-50"
                        onClick={() => setSinUnidadAbierta((v) => !v)}
                      >
                        <TableCell className="text-xs italic">
                          {sinUnidadAbierta ? (
                            <CaretDown className="h-3 w-3 inline mr-1" weight="bold" />
                          ) : (
                            <CaretRight className="h-3 w-3 inline mr-1" weight="bold" />
                          )}
                          Máquinas sin unidad
                          <span className="ml-2 not-italic font-normal">
                            (con ingreso o gasto en el período)
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-xs tabular-nums">
                          {sinUnidad.length}
                        </TableCell>
                        {celdasCifras(cifrasSin)}
                      </TableRow>
                      {sinUnidadAbierta &&
                        [...sinUnidad]
                          .sort((a, b) => b.totalGastoLcy - a.totalGastoLcy)
                          .map((m) => (
                            <TableRow key={m.codigoMaquina} className="bg-zinc-50/70">
                              <TableCell className="text-xs pl-8 text-muted-foreground">
                                {etiquetaMaquina(m)}
                              </TableCell>
                              <TableCell />
                              {celdasCifras(sumar([m]))}
                            </TableRow>
                          ))}
                    </>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
}

function VistaAdministrar({
  unidades,
  maquinas,
}: {
  unidades: UnidadProduccion[];
  maquinas: MaquinaUnidadProduccion[];
}) {
  const { crear, editar, borrar, asignar } = useUnidadesProduccionMutaciones();
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [editando, setEditando] = useState<{
    id: number;
    nombre: string;
    descripcion: string;
  } | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtroUnidad, setFiltroUnidad] = useState<'todas' | 'sin' | number>('todas');
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [destino, setDestino] = useState<string>('');

  const errorMsg = (e: unknown) =>
    e instanceof Error ? e.message.replace(/^HTTP \d+: /, '') : 'Error';

  const conteo = useMemo(() => {
    const m = new Map<number, number>();
    for (const q of maquinas) {
      if (q.skUnidadProduccion !== null) {
        m.set(q.skUnidadProduccion, (m.get(q.skUnidadProduccion) ?? 0) + 1);
      }
    }
    return m;
  }, [maquinas]);

  const nombreUnidad = useMemo(
    () => new Map(unidades.map((u) => [u.skUnidadProduccion, u.nombre])),
    [unidades],
  );

  const visibles = useMemo(() => {
    const b = normalizar(busqueda);
    return maquinas.filter((m) => {
      if (filtroUnidad === 'sin' && m.skUnidadProduccion !== null) return false;
      if (typeof filtroUnidad === 'number' && m.skUnidadProduccion !== filtroUnidad) return false;
      if (!b) return true;
      return (
        normalizar(m.codigoMaquina).includes(b) || normalizar(m.nombreMaquina ?? '').includes(b)
      );
    });
  }, [maquinas, busqueda, filtroUnidad]);

  const crearUnidad = () => {
    if (!nombre.trim()) return;
    crear.mutate(
      { nombre: nombre.trim(), descripcion: descripcion.trim() || null },
      {
        onSuccess: () => {
          toast.success(`Unidad "${nombre.trim()}" creada`);
          setNombre('');
          setDescripcion('');
        },
        onError: (e) => toast.error(errorMsg(e)),
      },
    );
  };

  const guardarEdicion = () => {
    if (!editando || !editando.nombre.trim()) return;
    editar.mutate(
      {
        id: editando.id,
        nombre: editando.nombre.trim(),
        descripcion: editando.descripcion.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success('Unidad actualizada');
          setEditando(null);
        },
        onError: (e) => toast.error(errorMsg(e)),
      },
    );
  };

  const borrarUnidad = (u: UnidadProduccion) => {
    const n = conteo.get(u.skUnidadProduccion) ?? 0;
    const aviso = n > 0 ? ` Sus ${n} máquinas quedarán sin unidad.` : '';
    if (!window.confirm(`¿Borrar la unidad "${u.nombre}"?${aviso}`)) return;
    borrar.mutate(u.skUnidadProduccion, {
      onSuccess: () => toast.success(`Unidad "${u.nombre}" borrada`),
      onError: (e) => toast.error(errorMsg(e)),
    });
  };

  const asignarA = (codigos: string[], sk: number | null) => {
    asignar.mutate(
      { codigosMaquina: codigos, skUnidadProduccion: sk },
      {
        onSuccess: (r) => {
          toast.success(
            sk === null
              ? `${r.data?.actualizadas ?? codigos.length} máquina(s) sin unidad`
              : `${r.data?.actualizadas ?? codigos.length} máquina(s) asignadas a "${nombreUnidad.get(sk)}"`,
          );
          setSeleccion(new Set());
        },
        onError: (e) => toast.error(errorMsg(e)),
      },
    );
  };

  const alternarSel = (codigo: string) =>
    setSeleccion((p) => {
      const n = new Set(p);
      if (n.has(codigo)) n.delete(codigo);
      else n.add(codigo);
      return n;
    });
  const todosVisibles =
    visibles.length > 0 && visibles.every((m) => seleccion.has(m.codigoMaquina));

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <Card className="lg:col-span-1 self-start">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Unidades</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2 rounded border border-zinc-200 p-3">
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && crearUnidad()}
              placeholder="Nombre (ej. Planta Blend)"
              maxLength={100}
              className="h-8 w-full rounded border border-zinc-300 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <input
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Descripción (opcional)"
              maxLength={400}
              className="h-8 w-full rounded border border-zinc-300 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <Button size="sm" onClick={crearUnidad} disabled={!nombre.trim() || crear.isPending}>
              Crear unidad
            </Button>
          </div>

          {unidades.length === 0 && (
            <p className="text-xs text-muted-foreground">Aún no hay unidades.</p>
          )}
          {unidades.map((u) =>
            editando?.id === u.skUnidadProduccion ? (
              <div
                key={u.skUnidadProduccion}
                className="space-y-2 rounded border border-blue-300 p-3"
              >
                <input
                  value={editando.nombre}
                  onChange={(e) => setEditando({ ...editando, nombre: e.target.value })}
                  maxLength={100}
                  className="h-8 w-full rounded border border-zinc-300 px-2 text-xs"
                />
                <input
                  value={editando.descripcion}
                  onChange={(e) => setEditando({ ...editando, descripcion: e.target.value })}
                  maxLength={400}
                  placeholder="Descripción"
                  className="h-8 w-full rounded border border-zinc-300 px-2 text-xs"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={guardarEdicion} disabled={editar.isPending}>
                    Guardar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditando(null)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div
                key={u.skUnidadProduccion}
                className="flex items-start justify-between gap-2 rounded border border-zinc-200 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{u.nombre}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {conteo.get(u.skUnidadProduccion) ?? 0} máquinas
                    {u.descripcion ? ` · ${u.descripcion}` : ''}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    className="rounded p-1 text-muted-foreground hover:bg-zinc-100"
                    title="Editar"
                    aria-label={`Editar ${u.nombre}`}
                    onClick={() =>
                      setEditando({
                        id: u.skUnidadProduccion,
                        nombre: u.nombre,
                        descripcion: u.descripcion ?? '',
                      })
                    }
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="rounded p-1 text-red-600 hover:bg-red-50"
                    title="Borrar"
                    aria-label={`Borrar ${u.nombre}`}
                    onClick={() => borrarUnidad(u)}
                  >
                    <Trash className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ),
          )}
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader className="pb-3 space-y-2">
          <CardTitle className="text-sm font-medium">
            Asignar máquinas ({visibles.length.toLocaleString('es-CR')})
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar máquina…"
              className="h-8 w-56 rounded border border-zinc-300 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <select
              value={String(filtroUnidad)}
              onChange={(e) => {
                const v = e.target.value;
                setFiltroUnidad(v === 'todas' || v === 'sin' ? v : Number(v));
              }}
              className="h-8 rounded border border-zinc-300 px-2 text-xs"
              aria-label="Filtrar por unidad"
            >
              <option value="todas">Todas las máquinas</option>
              <option value="sin">Sin unidad</option>
              {unidades.map((u) => (
                <option key={u.skUnidadProduccion} value={u.skUnidadProduccion}>
                  {u.nombre}
                </option>
              ))}
            </select>
          </div>
          {seleccion.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded bg-blue-50 px-3 py-2 text-xs">
              <span className="font-medium">{seleccion.size} seleccionadas</span>
              <select
                value={destino}
                onChange={(e) => setDestino(e.target.value)}
                className="h-8 rounded border border-zinc-300 px-2 text-xs"
                aria-label="Unidad destino"
              >
                <option value="">Asignar a…</option>
                {unidades.map((u) => (
                  <option key={u.skUnidadProduccion} value={u.skUnidadProduccion}>
                    {u.nombre}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                disabled={!destino || asignar.isPending}
                onClick={() => asignarA([...seleccion], Number(destino))}
              >
                Asignar
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={asignar.isPending}
                onClick={() => asignarA([...seleccion], null)}
              >
                Quitar de su unidad
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSeleccion(new Set())}>
                Limpiar
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto max-h-[640px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    checked={todosVisibles}
                    onChange={() =>
                      setSeleccion(
                        todosVisibles ? new Set() : new Set(visibles.map((m) => m.codigoMaquina)),
                      )
                    }
                    aria-label="Seleccionar todas las visibles"
                  />
                </TableHead>
                <TableHead className="text-xs">Máquina</TableHead>
                <TableHead className="text-xs text-right">Ingreso</TableHead>
                <TableHead className="text-xs text-right">Gasto</TableHead>
                <TableHead className="text-xs">Unidad</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibles.map((m) => (
                <TableRow key={m.codigoMaquina}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={seleccion.has(m.codigoMaquina)}
                      onChange={() => alternarSel(m.codigoMaquina)}
                      aria-label={`Seleccionar ${m.codigoMaquina}`}
                    />
                  </TableCell>
                  <TableCell className="text-xs">{etiquetaMaquina(m)}</TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {formatearCRC(m.totalIngresoLcy)}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {formatearCRC(m.totalGastoLcy)}
                  </TableCell>
                  <TableCell>
                    <select
                      value={m.skUnidadProduccion ?? ''}
                      disabled={asignar.isPending}
                      onChange={(e) =>
                        asignarA([m.codigoMaquina], e.target.value ? Number(e.target.value) : null)
                      }
                      className="h-7 w-44 rounded border border-zinc-300 px-1 text-xs"
                      aria-label={`Unidad de ${m.codigoMaquina}`}
                    >
                      <option value="">— sin unidad —</option>
                      {unidades.map((u) => (
                        <option key={u.skUnidadProduccion} value={u.skUnidadProduccion}>
                          {u.nombre}
                        </option>
                      ))}
                    </select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
